import { useState, useEffect } from "react";
import BarGraph from "../UI/BarGraph";
import { requestPartnerAPI } from "../../api/server";
import LoadingGraph from "../UI/LoadingGraph";
import DetailPair from "../UI/DetailPair";
import { formatCurrency } from "../../util/format-text";
import { isoDateToString } from "../../util/format-date";

/** ========================================================================
 * * Vehicles Revenue Graph
 * Renders a component, which displays a vehicle's revenue in a graph.
 * ========================================================================== */

export default function VehiclesRevenue({ vehicles }) {
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [data, setData] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);

  // ============================================= FETCH REVENUE FOR VEHICLES
  const fetchRevenueHandler = async () => {
    try {
      if (vehicles?.length < 1) {
        setLoading(false);
        return;
      }
      setLoading(true);
      const ids = vehicles?.map((v) => v.vin) || [];
      // Get Revenue
      const revenue = await requestPartnerAPI("get-vehicle-stats", {
        year: selectedYear,
        vehicles: ids,
      });

      // Get Payments
      let payments = await requestPartnerAPI("fetch-payments", {
        status: "succeeded",
      });

      // Get Stats
      const newStats = {};

      // Loop Vehicles
      vehicles.forEach((vehicle) => {
        newStats.totalInstallments =
          Number(newStats.totalInstallments || 0) +
          Number(vehicle.monthlyPayment || 0);
        newStats.totalFinanceAmount =
          Number(newStats.totalFinanceAmount || 0) +
          Number(vehicle.financeAmount || 0);
        newStats.totalAvgValue =
          Number(newStats.totalAvgValue || 0) + Number(vehicle.avgValue || 0);
        newStats.totalHighValue =
          Number(newStats.totalHighValue || 0) + Number(vehicle.highValue || 0);
        newStats.totalLowValue =
          Number(newStats.totalLowValue || 0) + Number(vehicle.lowValue || 0);
        newStats.totalDeductions =
          Number(newStats.totalDeductions || 0) +
          Number(vehicle.depreciationClaimed || 0);
      });

      // Reduce Revenue
      newStats.totalRevenue = revenue.reduce(
        (total, month) => total + (Number(month.revenue) || 0),
        0
      );

      // Loop Payments
      payments.forEach((payment) => {
        // Reduce Total Credits
        if (payment.category?.includes("rewards"))
          newStats.totalCredits =
            Number(newStats.totalCredits || 0) + Number(payment.amount || 0);

        // Reduce Total Installments
        if (
          payment.category?.includes("installment") &&
          payment.applicationId &&
          ids.includes(payment.applicationId.split("-")[1])
        )
          newStats.totalReceived =
            Number(newStats.totalReceived || 0) + Number(payment.amount);
      });

      // Calculate Loan to Value
      if (newStats.totalFinanceAmount && newStats.totalHighValue)
        newStats.loanToValue = (
          (Number(newStats.totalHighValue) /
            Number(newStats.totalFinanceAmount)) *
          100
        ).toFixed(2);
      newStats.proceeds = (
        Number(newStats.totalHighValue || 0) -
        Number(newStats.totalFinanceAmount || 0) +
        Number(newStats.totalCredits || 0) +
        Number(newStats?.totalDeductions || 0) +
        Number(newStats.totalReceived || 0)
      ).toFixed(2);
      setData(revenue);
      setStats(newStats);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (vehicles) fetchRevenueHandler();
  }, [selectedYear, vehicles]);

  return (
    <div>
      <div className="flex justify-end">
        <select
          className="text-sm bg-transparent border-b rounded-none cursor-pointer input"
          value={selectedYear}
          onChange={(e) => setSelectedYear(e.target.value)}
        >
          {Array.from(
            { length: new Date().getFullYear() - 2022 + 1 },
            (_, i) => {
              return (
                <option key={i} value={2022 + i}>
                  {2022 + i}
                </option>
              );
            }
          )}
        </select>
      </div>
      {loading ? <LoadingGraph height={250} /> : <BarGraph data={data} />}

      <div className="items-end info lg:grid-cols-5">
        {vehicles?.length > 1 && (
          <DetailPair
            label="Total Vehicles"
            value={vehicles.length}
            classes="text-xl text-stone-200 font-semibold"
            loading={loading}
          />
        )}
        <DetailPair
          label="Total Revenue Generated"
          value={formatCurrency(stats?.totalRevenue)}
          classes="text-xl font-semibold text-yellow-600"
          loading={loading}
        />
        <DetailPair
          label="Installments Paid"
          value={formatCurrency(stats?.totalReceived)}
          classes="text-xl font-semibold text-green-600"
          loading={loading}
        />
        {vehicles?.length > 1 && (
          <DetailPair
            label="Credits Earned"
            value={formatCurrency(stats?.totalCredits)}
            classes="text-xl font-semibold text-green-600"
            loading={loading}
          />
        )}
        <DetailPair
          label="Current Market Value"
          value={formatCurrency(stats?.totalHighValue)}
          classes="text-xl font-semibold text-teal-500"
          loading={loading}
        />
        <DetailPair
          label="Amount Financed"
          value={formatCurrency(stats?.totalFinanceAmount)}
          classes="text-xl font-semibold text-red-500"
          loading={loading}
        />
        {vehicles?.length > 1 && (
          <DetailPair
            label="Monthly Installments"
            value={formatCurrency(stats?.totalInstallments)}
            classes="text-xl font-semibold text-red-400"
            loading={loading}
          />
        )}
        {vehicles?.length === 1 && (
          <DetailPair
            label="Last Updated"
            value={isoDateToString(vehicles[0]?.lastValueCheck)}
            classes="text-xl font-semibold text-stone-400"
            loading={loading}
          />
        )}
        {vehicles?.length > 1 && (
          <DetailPair
            label="Depreciation Claimed"
            value={formatCurrency(stats?.totalDeductions)}
            classes="text-xl font-semibold text-green-600"
            loading={loading}
          />
        )}
        {vehicles?.length > 1 && (
          <DetailPair
            label="Net Liquidation Proceeds"
            value={formatCurrency(stats?.proceeds)}
            classes={`text-xl font-semibold ${
              stats?.proceeds < 0 ? "text-red-500" : "text-lime-500"
            }`}
            loading={loading}
          />
        )}
      </div>
      {stats?.totalHighValue > 0 && <div
        onClick={() => window.open("https://www.vinaudit.com", "_blank")}
        className="flex items-end justify-end mt-4 space-x-2 text-xs cursor-pointer text-stone-400 hover:underline"
      >
        <p>
          *The above market value is estimated using market data from 90 days
          past the last updated date - Powered by
        </p>
        <img
          src="https://www.vinaudit.com/wp-content/uploads/2024/03/vinaudit-logo-1-300x74.png"
          className="object-contain h-5"
        />
      </div>}
    </div>
  );
}

const StatsPair = ({ title, value, quantity, loading }) => {
  const format = (amount) => {
    if (amount) {
      const currency = new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }).format(Number(amount));
      return currency;
    } else return "0";
  };
  return (
    <div>
      <p className="text-xs text-stone-400">{title.toUpperCase()}</p>
      {loading ? (
        <div className="w-32 h-8 rounded-md animate-pulse bg-stone-600"> </div>
      ) : (
        <p className="text-2xl font-bold text-stone-200">
          {quantity || format(value)}
        </p>
      )}
    </div>
  );
};
